<!--
  @component AuthFlow
  @description Handles application initialization and authentication flow

  Responsibilities:
  - Initializes the application on mount
  - Handles proxy mode redirects
  - Shows authentication modal when needed
  - Provides authentication state to children

  Dispatches events:
  - initialized: when initialization is complete
-->
<script lang="ts">
  import { onMount, createEventDispatcher } from 'svelte';
  import { isAuthenticated } from '@stores/authStore';
  import {
    showAuthModal,
    showSettingsModal,
    isSettingsModalVisible,
    hideSettingsModal,
  } from '@stores/uiStore';
  import { initializeApp } from '@services/initializationService';
  import { initPreferences } from '@stores/preferencesStore';
  import AuthModal from './ui/AuthModal.svelte';
  import SettingsModal from './ui/SettingsModal.svelte';

  // ==========================================================================
  // STATE
  // ==========================================================================

  let isInitializing = true;
  let initError: string | null = null;

  const dispatch = createEventDispatcher<{
    initialized: { success: boolean };
  }>();

  // ==========================================================================
  // INITIALIZATION
  // ==========================================================================

  onMount(async () => {
    try {
      const result = await initializeApp();

      // Initialize user preferences from storage
      initPreferences();

      if (result.redirectUrl) {
        // Proxy mode: redirect to Portal for authentication
        window.location.href = result.redirectUrl;
        return;
      }

      if (result.needsAuth) {
        // Show auth modal for API key input
        showAuthModal();
      }

      isInitializing = false;
      dispatch('initialized', { success: true });
    } catch (error) {
      initError = error instanceof Error ? error.message : 'Initialization failed';
      isInitializing = false;
      dispatch('initialized', { success: false });
    }
  });

  // ==========================================================================
  // PUBLIC API
  // ==========================================================================

  export function openSettings() {
    showSettingsModal();
  }

  export { isInitializing, initError };
</script>

<!-- Auth Modal -->
<AuthModal />

<!-- Settings Modal -->
<SettingsModal visible={$isSettingsModalVisible} onClose={hideSettingsModal} />

<!-- Render children -->
<slot {isInitializing} {initError} authenticated={$isAuthenticated} />
